import React, { Fragment, memo } from 'react'
import tw from 'twin.macro'
import type { FormikHelpers } from 'formik'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import type { RemoteConfigApp } from 'src/constants/remote-config/remoteConfig'
import type {
  RemoteConfig,
  RemoteConfigFormMode,
  RemoteConfigFormValues,
} from 'src/interfaces/remoteConfig'
import { RemoteConfigForm } from './RemoteConfigForm'

const FORM_ID = 'remote-config-form'

/**
 * `CommonModal` has no close-icon slot, so the header sits at the top of its body and cancels the
 * body padding to run edge to edge. Not sticky: inside the padded body it would be pushed back down.
 */
const HEADER_CSS = tw`-mx-5 -mt-5 mb-5`

interface Props {
  isOpen: boolean
  mode: RemoteConfigFormMode
  remoteConfig: RemoteConfig | null
  defaultApp: RemoteConfigApp
  existingRemoteConfigs: RemoteConfig[]
  isSaving: boolean
  onSubmit(values: RemoteConfigFormValues, helpers: FormikHelpers<RemoteConfigFormValues>): void
  onClose(): void
}

const buildTitle = (mode: RemoteConfigFormMode, remoteConfig: RemoteConfig | null): string => {
  if (mode === 'create') {
    return 'New key'
  }

  if (mode === 'edit') {
    return `Edit ${remoteConfig?.key ?? ''}`
  }

  return remoteConfig?.key ?? ''
}

const RemoteConfigFormModalComponent = ({
  isOpen,
  mode,
  remoteConfig,
  defaultApp,
  existingRemoteConfigs,
  isSaving,
  onSubmit,
  onClose,
}: Props) => {
  const buttons =
    mode === 'view' ? (
      <Button size="small" color="grey" onClick={onClose}>
        Close
      </Button>
    ) : (
      <Fragment>
        <Button size="small" color="grey" disabled={isSaving} onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="small"
          color="dark"
          type="submit"
          form={FORM_ID}
          loading={isSaving}
          disabled={isSaving}
          data-testid="remote-config-form-submit-button"
        >
          {mode === 'edit' ? 'Save' : 'Create'}
        </Button>
      </Fragment>
    )

  return (
    <CommonModal isOpen={isOpen} className={tw`max-w-[560px]`} onClose={onClose} buttons={buttons}>
      <ModalHeader
        title={buildTitle(mode, remoteConfig)}
        className={HEADER_CSS}
        onClose={onClose}
        closeButtonTestId="remote-config-form-close-button"
      />
      <RemoteConfigForm
        formId={FORM_ID}
        mode={mode}
        remoteConfig={remoteConfig}
        defaultApp={defaultApp}
        existingRemoteConfigs={existingRemoteConfigs}
        onSubmit={onSubmit}
      />
    </CommonModal>
  )
}

export const RemoteConfigFormModal = memo(RemoteConfigFormModalComponent)
